"use client"

import type { ReactNode } from "react"
import { Badge } from "@cloudflare/kumo/components/badge"
import { Banner } from "@cloudflare/kumo/components/banner"
import { Button, type ButtonProps } from "@cloudflare/kumo/components/button"
import { LayerCard } from "@cloudflare/kumo/components/layer-card"
import { Text } from "@cloudflare/kumo/components/text"
import { AppTheme } from "@/components/kumo/theme"
import { AtlasGlobe } from "@/components/kumo/atlas-globe"

/**
 * The layout for pages outside a workspace: sign-in, sign-up, access denied,
 * and not found. One layered card sits centered over the atlas globe. The
 * secondary layer holds the wordmark and the "Private beta" badge; the
 * primary layer holds the title, the page content, and `footer`.
 */
export function StandaloneCard({
  label,
  title,
  footer,
  children,
}: {
  /** Accessible name of the region, such as "Sign in". */
  label: string
  title?: ReactNode
  footer?: ReactNode
  children: ReactNode
}) {
  return (
    <main className="relative isolate grid min-h-dvh w-full place-items-center overflow-hidden bg-kumo-recessed px-6 py-16 text-kumo-default">
      <AtlasGlobe className="absolute inset-0 -z-10 size-full" />
      <AppTheme role="region" className="contents" aria-label={label}>
        <LayerCard className="w-full max-w-[420px] shadow-md">
          <LayerCard.Secondary className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-kumo-strong">
              <span
                aria-hidden
                className="h-3.5 w-7 shrink-0 bg-current mask-contain mask-center mask-no-repeat"
                style={{ maskImage: "url('/brand/ortelian.svg')" }}
              />
              Ortelian
            </span>
            <Badge variant="secondary">Private beta</Badge>
          </LayerCard.Secondary>
          <LayerCard.Primary className="gap-6 px-6 py-5">
            {title ? <StandaloneTitle>{title}</StandaloneTitle> : null}
            {children}
            {footer ? <Text variant="secondary">{footer}</Text> : null}
          </LayerCard.Primary>
        </LayerCard>
      </AppTheme>
    </main>
  )
}

/** The page heading, for steps that change it inside the card. */
export function StandaloneTitle({ children }: { children: ReactNode }) {
  return (
    <Text variant="heading" size="lg" as="h1">
      {children}
    </Text>
  )
}

/** An error is announced at once; information is a polite status. */
export function StandaloneNote({
  tone,
  children,
}: {
  tone: "error" | "info"
  children: ReactNode
}) {
  return (
    <Banner
      variant={tone === "error" ? "error" : "secondary"}
      role={tone === "error" ? "alert" : "status"}
      description={children}
    />
  )
}

/** The card's full-width action; primary unless the page says otherwise. */
export function StandaloneButton({ className, ...props }: ButtonProps) {
  return (
    <Button
      size="lg"
      variant="primary"
      {...props}
      className={`w-full max-w-none justify-center ${className ?? ""}`}
    />
  )
}
